import {
  Box,
  ConfirmationDialogBox,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { CloseIcon, HelpCircleIcon } from '@atgfr8/icons';
import { AtgLoader } from '@components/AtgLoader';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { useNavigate } from '@solidjs/router';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import {
  getProspectsGridData,
  ProspectGridData,
  updateProspectStatus,
} from '@store/prospect';
import { userStore } from '@store/user';
import {
  closestCenter,
  CollisionDetector,
  createSortable,
  DragDropProvider,
  DragDropSensors,
  DragEventHandler,
  Draggable,
  DragOverlay,
  Droppable,
  Id,
  maybeTransformStyle,
  SortableProvider,
} from '@thisbeyond/solid-dnd';
import { routeProspectDetails } from '@utils/routeProspectDetails';
import { isAgent, isSupervisor } from '@utils/utils';
import { REMOVE_PROSPECT_DIALOG_ID } from '@views/order/constants';
import { DateTime } from 'luxon';
import {
  Accessor,
  batch,
  createEffect,
  createSignal,
  For,
  onMount,
  Setter,
  Show,
  VoidComponent,
} from 'solid-js';
import { VList } from 'virtua/solid';

export const ORDER_DELTA = 1000;

interface Base {
  id: Id;
  name: string;
  type: 'group' | 'item';
  classes?: string;
}

interface Group extends Base {
  type: 'group';
}

interface Item extends Base {
  type: 'item';
  item: ProspectGridData;
  group: Id;
}

type Entity = Group | Item;

const Item: VoidComponent<{
  id: Id;
  group: Id;
  item?: ProspectGridData;
  classes?: string;
  setItemToDelete: (id: Id | null) => void;
}> = (props) => {
  const sortable = createSortable(props.id, {
    type: 'item',
    group: props.group,
  });

  const navigate = useNavigate();

  const onProspectDelete = (e: Event) => {
    e.stopPropagation();
    props.setItemToDelete(props.id);
    openDialogBox(REMOVE_PROSPECT_DIALOG_ID);
  };

  return (
    <div
      ref={sortable}
      class={`sortable relative p-2 border rounded-md mb-2 cursor-pointer ${props.classes}`}
      classList={{ 'opacity-25': sortable.isActiveDraggable }}
      onClick={() => {
        const isNative = routeProspectDetails(String(props.item?.id));
        isNative && navigate(`/prospect/details/${props.item?.id}`);
      }}
    >
      <div class="absolute! right-2! top-1!" onClick={onProspectDelete}>
        <IconButton dataTestId={`prospect-delete-button-${props.id}`}>
          <CloseIcon class="size-5! text-[#B00020]" />
        </IconButton>
      </div>
      <div
        class="rounded-md p-[1.5px]"
        style={{ 'background-color': props.item?.color ?? 'transparent' }}
      />
      <Stack
        spacing={0.25}
        sx={{
          width: '84%',
        }}
      >
        <Typography
          variant="body1"
          class="font-medium! underline text-base!  wrap-break-word! decoration-[#ccc]"
        >
          {props.item?.name}
        </Typography>
        <Typography variant="body1" class="font-normal! text-xs!">
          Account Rep: {props.item?.createdBy}
        </Typography>
        <Typography variant="body1" class="font-normal! text-xs!">
          Last Contacted:{' '}
          {Boolean(props.item?.lastContactDate)
            ? DateTime.fromISO(`${props.item?.lastContactDate}Z`).toFormat(
                'MM/dd/yyyy',
              )
            : ''}
        </Typography>
        <Typography variant="body1" class="font-normal! text-xs!">
          Followup On:{' '}
          {Boolean(props.item?.followUpDate)
            ? DateTime.fromISO(`${props.item?.followUpDate}Z`).toFormat(
                'MM/dd/yyyy',
              )
            : ''}
        </Typography>
        <Typography variant="body1" class="font-normal! text-xs!">
          Created On:{' '}
          {Boolean(props.item?.createdDate)
            ? DateTime.fromISO(`${props.item?.createdDate}Z`)
                .toLocal()
                .toFormat('MM/dd/yyyy')
            : ''}
        </Typography>
      </Stack>
    </div>
  );
};

const ItemOverlay: VoidComponent<{ item: ProspectGridData | string }> = (
  props,
) => {
  if (typeof props.item === 'string') {
    return <div>{props.item}</div>;
  }

  return (
    <div
      class={`sortable relative p-2 border rounded-md mb-2 cursor-pointer
        bg-white border-[#aaa]`}
    >
      <div
        class="rounded-md p-[1.5px]"
        style={{ 'background-color': props.item.color ?? 'transparent' }}
      />
      <Stack
        spacing={0.25}
        sx={{
          width: '84%',
        }}
      >
        <Typography
          variant="body1"
          class="font-medium! underline text-base!  wrap-break-word! decoration-[#ccc]"
        >
          {props.item.name}
        </Typography>
        <Typography variant="body1" class="font-normal! text-xs!">
          Account Rep: {props.item.createdBy}
        </Typography>
        <Typography variant="body1" class="font-normal! text-xs!">
          Last Contacted:{' '}
          {Boolean(props.item.lastContactDate)
            ? DateTime.fromISO(`${props.item.lastContactDate}Z`).toFormat(
                'MM/dd/yyyy',
              )
            : ''}
        </Typography>
        <Typography variant="body1" class="font-normal! text-xs!">
          Followup On:{' '}
          {Boolean(props.item.followUpDate)
            ? DateTime.fromISO(`${props.item.followUpDate}Z`).toFormat(
                'MM/dd/yyyy',
              )
            : ''}
        </Typography>
        <Typography variant="body1" class="font-normal! text-xs!">
          Created On:{' '}
          {Boolean(props.item.createdDate)
            ? DateTime.fromISO(`${props.item.createdDate}Z`)
                .toLocal()
                .toFormat('MM/dd/yyyy')
            : ''}
        </Typography>
      </Stack>
    </div>
  );
};

const Group: VoidComponent<{
  id: Id;
  name: string;
  items: Item[];
  classes?: string;
  setItemToDelete: (id: Id | null) => void;
}> = (props) => {
  const sortable = createSortable(props.id, { type: 'group' });
  const sortedItemIds = () => props.items.map((item) => item.id);

  const groupTooltipText: { [key: number]: string } = {
    1: 'A Contact you wish to follow up with',
    2: 'You have reached out to this Lead and are communicating with them',
    3: 'You are Quoting/Bidding For Lanes',
    4: 'Customer has been submitted for credit',
  };

  return (
    <div
      ref={sortable.ref}
      class="w-[25%]!"
      style={maybeTransformStyle(sortable.transform)}
      classList={{ 'opacity-25': sortable.isActiveDraggable }}
    >
      <div
        class={`column-header border-blue-400 border-b-slate-600 rounded-md mb-2
        rounded-b-none relative
        text-center border ${props.classes}`}
        {...sortable.dragActivators}
      >
        {props.name} ({props.items.length})
        <span class="absolute right-[5px] top-[2px]">
          <Tooltip
            title={groupTooltipText[props.id as number]}
            class="z-10"
            placement={props.id === 4 ? 'left' : 'bottom'}
            dataTestId={`prospect-group-help-tooltip-${props.id}`}
          >
            <HelpCircleIcon size={18} class="text-[#1B4960]!" />
          </Tooltip>
        </span>
      </div>
      <div class="h-full">
        <SortableProvider ids={sortedItemIds()}>
          {/* <For each={props.items}>
            {(item) => (
              <Item
                id={item.id}
                group={item.group}
                item={item.item}
                classes={props.classes}
                setItemToDelete={props.setItemToDelete}
              />
            )}
          </For> */}
          <VList data={props.items}>
            {(item: Item) => (
              <Item
                id={item.id}
                group={item.group}
                item={item.item}
                classes={props.classes}
                setItemToDelete={props.setItemToDelete}
              />
            )}
          </VList>
        </SortableProvider>
      </div>
    </div>
  );
};

const GroupOverlay: VoidComponent<{ name: string; items: Item[] }> = (
  props,
) => {
  return (
    <div>
      <div class="column-header">{props.name}</div>
      <div class="column bg-gray-100">
        <For each={props.items}>
          {(item) => <ItemOverlay item={item.item} />}
        </For>
      </div>
    </div>
  );
};

const ListView = (props: {
  accountRep: string;
  prospectName: string;
  sortBy: Accessor<{
    field: string;
    order: 'asc' | 'desc';
    followUpStartDate: DateTime | null;
    followUpEndDate: DateTime | null;
  }>;
  activeFollowUpTab: Accessor<number>;
  refetch: Accessor<boolean>;
  setRefetch: Setter<boolean>;
}) => {
  const [loading, setLoading] = createSignal(true);
  const [entities, setEntities] = createSignal<Record<Id, Entity>>(
    {} as Record<Id, Entity>,
  );
  const [updateProspectStatusLoading, setUpdateProspectStatusLoading] =
    createSignal(false);
  const [itemToDelete, setItemToDelete] = createSignal<Id | null>(null);

  const [firstRender, setFirstRender] = createSignal(true);

  const addGroup = (id: Id, name: string, classes?: string) => {
    setEntities((prev) => ({
      ...prev,
      [id]: {
        id,
        name,
        classes,
        type: 'group' as const,
      } as Group,
    }));
  };

  const addItem = (
    id: Id,
    item: ProspectGridData,
    group: Id,
    classes?: string,
  ) => {
    setEntities((prev) => ({
      ...prev,
      [id]: {
        id,
        name: item.name,
        item,
        group,
        classes,
        type: 'item' as const,
      },
    }));
  };

  const removeItem = (id: Id) => {
    setEntities((prev) => {
      const newEntities = { ...prev };
      delete newEntities[id];
      return newEntities;
    });
  };

  const setup = async () => {
    try {
      setLoading(true);
      setEntities({});
      const filters = [
        { field: 'status', filterType: 'NOT_EQUALS', values: ['approved'] },
        { field: 'status', filterType: 'NOT_EQUALS', values: ['dead'] },
        { field: 'status', filterType: 'NOT_EQUALS', values: ['Dead Lead'] },
      ];

      [
        ['name', props.prospectName],
        ['createdBy', props.accountRep],
      ].forEach(([field, value]) => {
        if (value) {
          filters.push({
            field,
            filterType: 'STARTS_WITH',
            values: [value],
          });
        }
      });

      const sortFieldMap = {
        Name: 'name',
        'Follow Up Date': 'followUpDate',
        'Last Contact Date': 'lastContactDate',
        'Created Date': 'createdDate',
      };

      const param = {
        filters,
        sort: {
          field: firstRender()
            ? 'createdDate'
            : sortFieldMap[props.sortBy().field as keyof typeof sortFieldMap],
          direction: firstRender() ? 'desc' : props.sortBy().order,
        },
        pageNumber: 1,
        pageSize: 1000,
      };

      const res = await getProspectsGridData(param);

      if (res) {
        const filteredData = res.value.data.sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        if (isAgent() && !isSupervisor()) {
          filteredData.filter((item) => item.createdBy === userStore.user.name);
        }

        const groups = [
          { id: 1, name: 'Lead', classes: 'bg-[#F0F8FF] border-blue-400' },
          { id: 2, name: 'Contacted', classes: 'bg-[#FFFFFF] border-[#aaa]' },
          { id: 3, name: 'Quoting', classes: 'bg-[#FFFFC1] border-[#e2ca1b]' },
          {
            id: 4,
            name: 'Submitted',
            classes: 'bg-[#9ACC9A] border-[#29c31f]',
          },
        ];

        batch(() => {
          groups.forEach((group) =>
            addGroup(group.id, group.name, group.classes),
          );
          filteredData.forEach((item) => {
            const group = groups.find((g) => g.name === item.status);
            if (group) {
              addItem(item.id, item, group.id, group.classes);
            }
          });
        });
      }
    } finally {
      setLoading(false);
      setFirstRender(false);
    }
  };

  onMount(setup);

  createEffect(async () => {
    if (Boolean(props.refetch())) {
      await setup();
      props.setRefetch(false);
    }
  });

  const groups = (): Group[] =>
    Object.values(entities()).filter(
      (item): item is Group => item.type === 'group',
    );

  const groupIds = () => groups().map((group) => group.id);

  const groupItems = (groupId: Id) => {
    setUpdateProspectStatusLoading(true);
    setTimeout(() => {
      setUpdateProspectStatusLoading(false);
    }, 1000);

    const { sortBy } = props;
    const { followUpStartDate, followUpEndDate, field, order } = sortBy();

    const filteredEntities = Object.values(entities()).filter(
      (entity): entity is Item => {
        if (entity.type !== 'item' || entity.group !== groupId) return false;

        const { followUpDate } = entity.item;

        if (followUpStartDate && followUpEndDate) {
          const followUpTime = Boolean(followUpDate)
            ? new Date(followUpDate!).getTime()
            : null;
          if (
            followUpTime === null ||
            followUpTime < followUpStartDate.toMillis() ||
            followUpTime > followUpEndDate.toMillis()
          ) {
            return false;
          }
        }

        return true;
      },
    );

    const compare = (a: string, b: string) =>
      order === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
    const compareDates = (a: string, b: string) =>
      order === 'asc'
        ? new Date(a).getTime() - new Date(b).getTime()
        : new Date(b).getTime() - new Date(a).getTime();

    const sortFunctions: Record<string, (a: Item, b: Item) => number> = {
      Name: (a, b) => compare(a.item.name, b.item.name),
      'Last Contact Date': (a, b) =>
        compareDates(a.item.lastContactDate!, b.item.lastContactDate!),
      'Follow Up Date': (a, b) =>
        compareDates(a.item.followUpDate!, b.item.followUpDate!),
      'Created Date': (a, b) =>
        compareDates(a.item.createdDate, b.item.createdDate),
    };

    return filteredEntities.sort(sortFunctions[field]);
  };

  const isSortableGroup = (sortable: Draggable | Droppable) =>
    sortable.data.type === 'group';

  const move = (
    draggable: Draggable,
    droppable: Droppable,
    onlyWhenChangingGroup = true,
  ) => {
    const draggableIsGroup = isSortableGroup(draggable);
    const droppableIsGroup = isSortableGroup(droppable);

    const draggableGroupId = draggableIsGroup
      ? draggable.id
      : draggable.data.group;
    const droppableGroupId = droppableIsGroup
      ? droppable.id
      : droppable.data.group;

    if (
      (onlyWhenChangingGroup &&
        (draggableIsGroup || draggableGroupId === droppableGroupId)) ||
      draggableGroupId === 4 ||
      droppableGroupId === 4
    ) {
      return;
    }
    setEntities((prev) => ({
      ...prev,
      [draggable.id]: {
        ...prev[draggable.id],
        group: droppableGroupId,
      },
    }));
  };

  const onDragEnd: DragEventHandler = async ({ draggable, droppable }) => {
    if (
      !droppable ||
      droppable.data.group === 4 ||
      draggable.data.group === 4
    ) {
      return;
    }

    setUpdateProspectStatusLoading(true);

    const entity = entities()[draggable.id];
    const statusMap = {
      1: 'Lead',
      2: 'Contacted',
      3: 'Quoting',
    };
    const newStatus =
      droppable.data.type === 'item'
        ? statusMap[droppable.data.group as keyof typeof statusMap]
        : statusMap[droppable.id as keyof typeof statusMap];
    if (newStatus) {
      const res = await updateProspectStatus(
        {
          id: (entity as Item).item.id,
          status: newStatus as unknown as
            | 'Lead'
            | 'Contacted'
            | 'Quoting'
            | 'Dead Lead',
        },
        [CHANGE_TRACKING_SOURCES.prospects.statusUpdate],
      );
      Boolean(res) && move(draggable, droppable);
    }
    setUpdateProspectStatusLoading(false);
  };

  const onProspectDelete = async () => {
    const id = itemToDelete();
    if (id === null) return;
    const res = await updateProspectStatus(
      {
        id: id as number,
        status: 'Dead Lead',
      },
      [CHANGE_TRACKING_SOURCES.prospects.statusUpdate],
    );
    if (Boolean(res)) {
      setItemToDelete(null);
      removeItem(id);
    }
  };

  const closestEntity = (
    draggable: Draggable,
    droppables: Droppable[],
    context: {
      activeDroppableId: Id | null;
    },
  ) => {
    const closestGroup = closestCenter(
      draggable,
      droppables.filter((droppable) => isSortableGroup(droppable)),
      context,
    );
    if (isSortableGroup(draggable)) {
      return closestGroup;
    } else if (closestGroup) {
      const closestItem = closestCenter(
        draggable,
        droppables.filter(
          (droppable) =>
            !isSortableGroup(droppable) &&
            droppable.data.group === closestGroup.id,
        ),
        context,
      );

      if (!closestItem) {
        return closestGroup;
      }

      const changingGroup = draggable.data.group !== closestGroup.id;
      if (changingGroup) {
        const belowLastItem =
          draggable.transformed.center.y > closestItem.transformed.center.y;

        if (belowLastItem) return closestGroup;
      }

      return closestItem;
    }
  };

  return (
    <Show
      when={!loading()}
      fallback={
        <Box
          class="flex gap-2 flex-1 pt-1 self-stretch"
          style={{ 'background-color': 'white' }}
        >
          <For each={Array(4).fill(0)}>
            {() => (
              <Stack gap={2} flex={1}>
                <Skeleton variant="rectangular" width="100%" height={40} />
                <For each={Array(4).fill(0)}>
                  {() => (
                    <Skeleton variant="rectangular" width="100%" height={117} />
                  )}
                </For>
              </Stack>
            )}
          </For>
        </Box>
      }
    >
      <div class="flex gap-2 pt-2" style={{ height: 'calc(100% - 85px)' }}>
        <Show when={updateProspectStatusLoading()}>
          <AtgLoader />
        </Show>
        <DragDropProvider
          onDragEnd={onDragEnd}
          collisionDetector={closestEntity as unknown as CollisionDetector}
        >
          <DragDropSensors />
          <SortableProvider ids={groupIds()}>
            <For each={groups()}>
              {(group) => (
                <Group
                  id={group.id}
                  name={group.name}
                  items={groupItems(group.id)}
                  classes={group.classes}
                  setItemToDelete={setItemToDelete}
                />
              )}
            </For>
          </SortableProvider>
          <DragOverlay>
            {(draggable) => {
              if (draggable) {
                const entity = entities()[draggable.id];
                return isSortableGroup(draggable) ? (
                  <GroupOverlay
                    name={entity.name}
                    items={groupItems(entity.id)}
                  />
                ) : (
                  <ItemOverlay
                    item={entity.type === 'item' ? entity.item : ''}
                  />
                );
              }
              return null;
            }}
          </DragOverlay>
        </DragDropProvider>
      </div>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(REMOVE_PROSPECT_DIALOG_ID)}
        dataTestId={REMOVE_PROSPECT_DIALOG_ID}
        id={REMOVE_PROSPECT_DIALOG_ID}
        title={(() => {
          const id = itemToDelete();
          const entity =
            id !== null ? (entities()[id] as Entity | undefined) : undefined;
          const label = entity?.type === 'item' ? entity.name : 'prospect';
          return `Are you sure you want to delete this ${label} ?`;
        })()}
        onSubmit={onProspectDelete}
        onSubmitText="Ok"
        closeDialogBox={(_reason) => {
          closeDialogBox(REMOVE_PROSPECT_DIALOG_ID);
          setItemToDelete(null);
        }}
      />
    </Show>
  );
};

export default ListView;
